import { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ArrowLeft, ArrowRight, Download, LayoutGrid, MoreHorizontal, Pencil, Plus, RotateCcw,
  Save, Search, Trash2, Upload, X, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { parseUserFile, downloadCsv, ACCEPTED_FILE_TYPES } from "@/lib/file-parsing";
import { useSavedCharts } from "@/hooks/useSavedCharts";
import {
  applyFilters, autoLayout, groupBy, newTileId, previousPeriodFilters, profileColumns,
  aggLabel, type Aggregation, type DashboardFilters, type TileSpec, type TileType,
} from "@/lib/dashboard/engine";
import { StudioChart } from "./StudioChart";

const TILE_TYPES: { value: TileType; label: string }[] = [
  { value: "kpi", label: "KPI scorecard" }, { value: "bar", label: "Bar" },
  { value: "stacked", label: "Stacked bar" }, { value: "line", label: "Line" },
  { value: "area", label: "Area" }, { value: "combo", label: "Combo (bar + line)" },
  { value: "pie", label: "Donut" }, { value: "funnel", label: "Funnel" },
  { value: "scatter", label: "Scatter" }, { value: "gauge", label: "Gauge vs target" },
  { value: "heatmap", label: "Heatmap table" }, { value: "table", label: "Data table" },
];
const AGGS: Aggregation[] = ["sum", "avg", "count", "min", "max", "distinct"];
const NONE = "__none__";
const storageKey = (src: string) => `veylo-studio:${src}`;

interface Dataset { name: string; key: string; rows: Record<string, any>[] }

export function DashboardStudio() {
  const [dataset, setDataset] = useState<Dataset | null>(null);
  const [files, setFiles] = useState<{ id: string; file_name: string; file_path: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [tiles, setTiles] = useState<TileSpec[]>([]);
  const [filters, setFilters] = useState<DashboardFilters>({ equals: {} });
  const [editing, setEditing] = useState<TileSpec | null>(null);
  const [drill, setDrill] = useState<{ column: string; value: string } | null>(null);
  const [search, setSearch] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);
  const { saveChart } = useSavedCharts();

  const profiles = useMemo(() => profileColumns(dataset?.rows ?? []), [dataset]);
  const dateCols = profiles.filter((p) => p.type === "date");
  const catCols = profiles.filter((p) => p.type === "category");
  const numCols = profiles.filter((p) => p.type === "number");

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase.from("file_storage")
        .select("id,file_name,file_path").eq("user_id", user.id).eq("is_folder", false)
        .or("file_name.ilike.%.csv,file_name.ilike.%.xlsx,file_name.ilike.%.xls")
        .order("created_at", { ascending: false }).limit(50);
      setFiles(data ?? []);
    })();
  }, []);

  // Persist layout + filters per dataset so a reload brings the same dashboard back.
  useEffect(() => {
    if (dataset) localStorage.setItem(storageKey(dataset.key), JSON.stringify({ tiles, filters }));
  }, [tiles, filters, dataset]);

  const openDataset = (ds: Dataset) => {
    if (!ds.rows.length) { toast.error("This file has no table rows to chart."); return; }
    setDataset(ds);
    const saved = localStorage.getItem(storageKey(ds.key));
    const prof = profileColumns(ds.rows);
    if (saved) {
      try {
        const s = JSON.parse(saved);
        setTiles(s.tiles ?? autoLayout(prof));
        setFilters(s.filters ?? { equals: {} });
        return;
      } catch { /* fall through to fresh layout */ }
    }
    const firstDate = prof.find((p) => p.type === "date")?.name;
    setTiles(autoLayout(prof));
    setFilters({ equals: {}, dateColumn: firstDate });
  };

  const loadStored = async (id: string) => {
    const f = files.find((x) => x.id === id);
    if (!f) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.storage.from("user_files").download(f.file_path);
      if (error) throw error;
      const parsed = await parseUserFile(new File([data], f.file_name, { type: data.type }));
      openDataset({ name: f.file_name, key: `file:${f.id}`, rows: parsed.rows ?? [] });
    } catch (e: any) {
      toast.error(`Could not open ${f.file_name}: ${e?.message ?? "unknown error"}`);
    } finally { setLoading(false); }
  };

  const loadUpload = async (file: File) => {
    setLoading(true);
    try {
      const parsed = await parseUserFile(file);
      if (parsed.kind !== "table") throw new Error("Use a CSV or Excel file for dashboards.");
      openDataset({ name: file.name, key: `upload:${file.name}:${file.size}`, rows: parsed.rows ?? [] });
    } catch (e: any) {
      toast.error(e?.message ?? "Could not read that file");
    } finally { setLoading(false); }
  };

  const rows = dataset?.rows ?? [];
  const filtered = useMemo(() => applyFilters(rows, filters), [rows, filters]);
  const prevFilters = useMemo(() => previousPeriodFilters(filters), [filters]);
  const previous = useMemo(() => (prevFilters ? applyFilters(rows, prevFilters) : null), [rows, prevFilters]);

  const toggleValue = (column: string, value: string) =>
    setFilters((f) => {
      const cur = f.equals[column] ?? [];
      const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
      return { ...f, equals: { ...f.equals, [column]: next } };
    });

  const activeChips = Object.entries(filters.equals).flatMap(([c, vs]) => vs.map((v) => ({ c, v })));
  const updateTile = (t: TileSpec) => setTiles((all) => all.map((x) => (x.id === t.id ? t : x)));
  const move = (id: string, dir: -1 | 1) => setTiles((all) => {
    const i = all.findIndex((t) => t.id === id); const j = i + dir;
    if (i < 0 || j < 0 || j >= all.length) return all;
    const copy = [...all]; [copy[i], copy[j]] = [copy[j], copy[i]]; return copy;
  });

  const exportTile = (t: TileSpec) => {
    const data = t.type === "table" || t.type === "scatter" || t.type === "kpi"
      ? filtered : groupBy(filtered, t, profiles).points;
    downloadCsv(`${t.title.replace(/[^\w-]+/g, "_")}.csv`, data);
  };

  const saveTile = (t: TileSpec) => {
    const { points } = groupBy(filtered, t, profiles);
    const chartType = (["bar", "line", "pie", "scatter", "area", "kpi"].includes(t.type) ? t.type : "bar") as any;
    const data = t.type === "kpi" || t.type === "scatter" || t.type === "table" ? filtered.slice(0, 500) : points;
    saveChart.mutate(
      {
        title: t.title, chartType, xField: t.dimension ?? null, yField: t.measure ?? null,
        aggregation: t.aggregation, data, origin: "generator",
        config: { studio: true, spec: t, filters }, sourceType: "file", sourceName: dataset?.name ?? null,
      },
      { onSuccess: () => toast.success(`Saved "${t.title}" to your dashboard`) },
    );
  };

  if (!dataset) {
    return (
      <Card className="p-8">
        <div className="mx-auto max-w-xl space-y-5 text-center">
          <LayoutGrid className="mx-auto h-10 w-10 text-primary" />
          <div>
            <h2 className="font-display text-xl font-semibold">Dashboard Studio</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Open one of your files to build an interactive dashboard: filters, click-to-filter charts, drill-down and comparisons with the previous period.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Select onValueChange={loadStored} disabled={loading || !files.length}>
              <SelectTrigger className="sm:w-72"><SelectValue placeholder={files.length ? "Choose a saved file" : "No saved CSV/Excel files yet"} /></SelectTrigger>
              <SelectContent>{files.map((f) => <SelectItem key={f.id} value={f.id}>{f.file_name}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" onClick={() => fileInput.current?.click()} disabled={loading}>
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Upload CSV / Excel
            </Button>
            <input ref={fileInput} type="file" accept={ACCEPTED_FILE_TYPES} className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) loadUpload(f); e.target.value = ""; }} />
          </div>
        </div>
      </Card>
    );
  }

  const drillRows = drill
    ? filtered.filter((r) => String(r[drill.column] ?? "").includes(drill.value) || String(r[drill.column] ?? "") === drill.value)
        .filter((r) => !search || Object.values(r).some((v) => String(v).toLowerCase().includes(search.toLowerCase())))
    : [];

  return (
    <div className="space-y-4">
      {/* Toolbar + global filters */}
      <Card className="space-y-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="mr-auto min-w-0">
            <h2 className="truncate font-display text-lg font-semibold">{dataset.name}</h2>
            <p className="text-xs text-muted-foreground">
              {filtered.length.toLocaleString()} of {rows.length.toLocaleString()} rows · {profiles.length} columns
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={() => setEditing({ id: newTileId(), type: "bar", title: "New chart", aggregation: "sum", size: 1, dimension: catCols[0]?.name, measure: numCols[0]?.name })}>
            <Plus className="mr-1 h-4 w-4" />Add chart
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setTiles(autoLayout(profiles)); toast.success("Layout rebuilt from your columns"); }}>
            <RotateCcw className="mr-1 h-4 w-4" />Auto layout
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setDataset(null); setTiles([]); }}>Change data</Button>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          {dateCols.length > 0 && (
            <>
              <div className="space-y-1">
                <Label className="text-xs">Date column</Label>
                <Select value={filters.dateColumn ?? NONE} onValueChange={(v) => setFilters((f) => ({ ...f, dateColumn: v === NONE ? undefined : v }))}>
                  <SelectTrigger className="h-8 w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>None</SelectItem>
                    {dateCols.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">From</Label>
                <Input type="date" className="h-8 w-36" value={filters.from ?? ""} onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value || undefined }))} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">To</Label>
                <Input type="date" className="h-8 w-36" value={filters.to ?? ""} onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value || undefined }))} />
              </div>
            </>
          )}
          {catCols.slice(0, 4).map((c) => {
            const values = Array.from(new Set(rows.map((r) => String(r[c.name] ?? "")))).filter(Boolean).sort().slice(0, 100);
            return (
              <div key={c.name} className="space-y-1">
                <Label className="text-xs">{c.name}</Label>
                <Select value="" onValueChange={(v) => toggleValue(c.name, v)}>
                  <SelectTrigger className="h-8 w-36"><SelectValue placeholder={filters.equals[c.name]?.length ? `${filters.equals[c.name].length} selected` : "All"} /></SelectTrigger>
                  <SelectContent>{values.map((v) => <SelectItem key={v} value={v}>{(filters.equals[c.name] ?? []).includes(v) ? "✓ " : ""}{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            );
          })}
        </div>

        {(activeChips.length > 0 || filters.from || filters.to) && (
          <div className="flex flex-wrap items-center gap-1.5">
            {(filters.from || filters.to) && (
              <Badge variant="secondary" className="gap-1">{filters.from ?? "…"} → {filters.to ?? "…"}
                <button aria-label="Clear dates" onClick={() => setFilters((f) => ({ ...f, from: undefined, to: undefined }))}><X className="h-3 w-3" /></button>
              </Badge>
            )}
            {activeChips.map(({ c, v }) => (
              <Badge key={c + v} variant="secondary" className="gap-1">{c}: {v}
                <button aria-label={`Remove ${v}`} onClick={() => toggleValue(c, v)}><X className="h-3 w-3" /></button>
              </Badge>
            ))}
            <Button size="sm" variant="link" className="h-6 px-1" onClick={() => setFilters((f) => ({ ...f, equals: {}, from: undefined, to: undefined }))}>Clear all</Button>
          </div>
        )}
      </Card>

      {/* Tile grid */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {tiles.map((t, i) => {
          const span = t.size === 3 ? "md:col-span-3" : t.size === 2 ? "md:col-span-2" : "";
          const height = t.type === "kpi" ? "h-24" : t.type === "table" ? "h-80" : t.type === "heatmap" ? "max-h-80 overflow-auto" : "h-64";
          const dimProfile = profiles.find((p) => p.name === t.dimension);
          const canCross = dimProfile?.type === "category";
          return (
            <Card key={t.id} className={`flex flex-col p-4 ${span}`}>
              <div className="mb-2 flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-sm font-medium">{t.title}</h3>
                  {t.type !== "kpi" && t.type !== "table" && canCross && <p className="text-[11px] text-muted-foreground">Click a value to filter the whole dashboard</p>}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Tile options"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setEditing(t)}><Pencil className="mr-2 h-4 w-4" />Edit</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => updateTile({ ...t, size: ((t.size % 3) + 1) as 1 | 2 | 3 })}><LayoutGrid className="mr-2 h-4 w-4" />Width: {t.size === 3 ? "small" : t.size === 1 ? "medium" : "full"}</DropdownMenuItem>
                    <DropdownMenuItem disabled={i === 0} onClick={() => move(t.id, -1)}><ArrowLeft className="mr-2 h-4 w-4" />Move earlier</DropdownMenuItem>
                    <DropdownMenuItem disabled={i === tiles.length - 1} onClick={() => move(t.id, 1)}><ArrowRight className="mr-2 h-4 w-4" />Move later</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => exportTile(t)}><Download className="mr-2 h-4 w-4" />Download CSV</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => saveTile(t)}><Save className="mr-2 h-4 w-4" />Save to my dashboard</DropdownMenuItem>
                    <DropdownMenuItem className="text-destructive" onClick={() => setTiles((all) => all.filter((x) => x.id !== t.id))}><Trash2 className="mr-2 h-4 w-4" />Remove</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className={height}>
                <StudioChart
                  spec={t}
                  rows={canCross ? applyFilters(rows, filters, t.dimension) : filtered}
                  previousRows={previous}
                  profiles={profiles}
                  selected={t.dimension ? filters.equals[t.dimension] ?? [] : []}
                  onSelect={(v) => {
                    if (!t.dimension) return;
                    if (canCross) toggleValue(t.dimension, v);
                    else setDrill({ column: t.dimension, value: v });
                  }}
                />
              </div>
              {canCross && (filters.equals[t.dimension!] ?? []).length > 0 && (
                <Button size="sm" variant="link" className="mt-1 h-6 self-start px-0 text-xs"
                  onClick={() => setDrill({ column: t.dimension!, value: filters.equals[t.dimension!][0] })}>
                  See rows for {filters.equals[t.dimension!][0]}
                </Button>
              )}
            </Card>
          );
        })}
      </div>

      {/* Chart editor side panel */}
      <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader><SheetTitle>Chart settings</SheetTitle></SheetHeader>
          {editing && (
            <TileEditor
              spec={editing}
              profiles={profiles}
              onChange={setEditing}
              onSave={() => {
                setTiles((all) => (all.some((t) => t.id === editing.id) ? all.map((t) => (t.id === editing.id ? editing : t)) : [...all, editing]));
                setEditing(null);
              }}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Drill-down */}
      <Dialog open={!!drill} onOpenChange={(o) => { if (!o) { setDrill(null); setSearch(""); } }}>
        <DialogContent className="max-h-[85vh] max-w-4xl overflow-hidden">
          <DialogHeader><DialogTitle>{drill?.column}: {drill?.value} — {drillRows.length.toLocaleString()} rows</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input className="pl-8" placeholder="Search these rows" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Button variant="outline" onClick={() => downloadCsv(`${drill?.value ?? "rows"}.csv`, drillRows)}><Download className="mr-2 h-4 w-4" />CSV</Button>
          </div>
          <div className="h-[55vh]">
            <StudioChart spec={{ id: "drill", type: "table", title: "", aggregation: "count", size: 3 }} rows={drillRows} profiles={profiles} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TileEditor({ spec, profiles, onChange, onSave }: {
  spec: TileSpec; profiles: ReturnType<typeof profileColumns>;
  onChange: (s: TileSpec) => void; onSave: () => void;
}) {
  const set = (patch: Partial<TileSpec>) => onChange({ ...spec, ...patch });
  const groupable = profiles.filter((p) => p.type === "category" || p.type === "date");
  const nums = profiles.filter((p) => p.type === "number");
  const cats = profiles.filter((p) => p.type === "category");
  const isDateDim = profiles.find((p) => p.name === spec.dimension)?.type === "date";
  const needsDim = !["kpi", "scatter", "gauge", "table"].includes(spec.type);

  const field = (label: string, value: string | undefined, options: { name: string }[], onPick: (v?: string) => void, allowNone = true) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Select value={value ?? NONE} onValueChange={(v) => onPick(v === NONE ? undefined : v)}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>
          {allowNone && <SelectItem value={NONE}>{label.includes("Value") ? "Count rows" : "None"}</SelectItem>}
          {options.map((o) => <SelectItem key={o.name} value={o.name}>{o.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="mt-4 space-y-4">
      <div className="space-y-1"><Label className="text-xs">Title</Label><Input value={spec.title} onChange={(e) => set({ title: e.target.value })} /></div>
      <div className="space-y-1">
        <Label className="text-xs">Chart type</Label>
        <Select value={spec.type} onValueChange={(v) => set({ type: v as TileType })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{TILE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {needsDim && field("Group by", spec.dimension, groupable, (v) => set({ dimension: v }), false)}
      {needsDim && isDateDim && (
        <div className="space-y-1">
          <Label className="text-xs">Time grain</Label>
          <Select value={spec.grain ?? "month"} onValueChange={(v) => set({ grain: v as any })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{["day", "week", "month", "quarter", "year"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      {spec.type !== "table" && field(spec.type === "scatter" ? "X value" : "Value", spec.measure, nums, (v) => set({ measure: v }), spec.type !== "scatter")}
      {(spec.type === "combo" || spec.type === "scatter") && field(spec.type === "scatter" ? "Y value" : "Line value", spec.measure2, nums, (v) => set({ measure2: v }))}
      {(spec.type === "stacked" || spec.type === "heatmap") && field("Break down by", spec.series, cats, (v) => set({ series: v }))}
      {spec.type !== "table" && spec.type !== "scatter" && spec.measure && (
        <div className="space-y-1">
          <Label className="text-xs">Calculation</Label>
          <Select value={spec.aggregation} onValueChange={(v) => set({ aggregation: v as Aggregation })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{AGGS.map((a) => <SelectItem key={a} value={a}>{aggLabel[a]}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      {needsDim && !isDateDim && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Sort</Label>
            <Select value={spec.sort ?? "value-desc"} onValueChange={(v) => set({ sort: v as any })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="value-desc">Highest first</SelectItem>
                <SelectItem value="value-asc">Lowest first</SelectItem>
                <SelectItem value="label">A → Z</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Show top</Label>
            <Input type="number" min={1} max={100} value={spec.topN ?? ""} placeholder="All" onChange={(e) => set({ topN: e.target.value ? Number(e.target.value) : undefined })} />
          </div>
        </div>
      )}
      {spec.type === "gauge" && (
        <div className="space-y-1"><Label className="text-xs">Target</Label>
          <Input type="number" value={spec.target ?? ""} onChange={(e) => set({ target: e.target.value ? Number(e.target.value) : undefined })} />
        </div>
      )}
      <Button className="w-full" onClick={onSave} disabled={needsDim && !spec.dimension}>Apply</Button>
    </div>
  );
}
